import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import VoiceAssistant from '@/components/VoiceAssistant';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  Calendar,
  Activity,
  UsersRound,
  Target,
  Megaphone,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Package,
  FileText,
  MapPin,
  Shield,
  Route,
  BarChart3,
  Radio,
  Car,
  Clock
} from 'lucide-react';

const EP3D_LOGO = "/images/ep3d-logo.png";

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/contacts', icon: Users, label: 'Clientes', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/clients-map', icon: MapPin, label: 'Mapa Clientes', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/opportunities', icon: TrendingUp, label: 'Oportunidades', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/orders', icon: FileText, label: 'Encomendas', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/routes', icon: Route, label: 'Rotas', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/shift', icon: Clock, label: 'Turno', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/shift-reports', icon: BarChart3, label: 'Relatórios Turnos', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/calendar', icon: Calendar, label: 'Calendário', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/activities', icon: Activity, label: 'Atividades', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/reports', icon: BarChart3, label: 'Relatórios', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/team', icon: UsersRound, label: 'Equipa', roles: ['admin', 'gestor'] },
  { path: '/location-tracking', icon: Radio, label: 'Localização Equipa', roles: ['admin'] },
  { path: '/vehicles', icon: Car, label: 'Viaturas', roles: ['admin'] },
  { path: '/goals', icon: Target, label: 'Objetivos', roles: ['admin', 'gestor', 'vendedor'] },
  { path: '/campaigns', icon: Megaphone, label: 'Campanhas', roles: ['admin', 'gestor'] },
  { path: '/products', icon: Package, label: 'Produtos', roles: ['admin'] },
  { path: '/admin', icon: Shield, label: 'Administração', roles: ['admin'] },
  { path: '/settings', icon: Settings, label: 'Configurações', roles: ['admin', 'gestor', 'vendedor'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userRole = user?.role || 'vendedor';
  
  // Enable location tracking for all logged-in users
  useLocationTracking(true, 60000); // Send location every 60 seconds
  
  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(userRole)
  );

  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item?.label || 'Dashboard';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'gestor': return 'Gestor Comercial';
      case 'vendedor': return 'Vendedor';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-white
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <img 
                src={EP3D_LOGO} 
                alt="EP3D" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="font-bold font-[Manrope] text-lg text-amber-400">VENDAS</h1>
              </div>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-slate-800 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-amber-500/20 text-amber-400 font-medium' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.label}</span>
                  {item.path === location.pathname && (
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  )}
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="h-10 w-10 border-2 border-slate-600">
                <AvatarFallback className="bg-amber-500 text-slate-900 text-sm font-bold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{getRoleLabel(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold font-[Manrope] text-slate-900 dark:text-white">
                  {getPageTitle()}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-indigo-100 text-indigo-800 text-xs">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.name}</span>
                      <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => window.location.href = '/settings'}>
                    <Settings className="mr-2 h-4 w-4" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Terminar sessão
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Voice Assistant - Floating */}
      <VoiceAssistant />
    </div>
  );
}
